/**
 * services/db/operations/qualityAssurance.ts
 *
 * Firebase write operations for the Quality Assurance domain.
 * Components should call these functions instead of calling Firebase directly.
 *
 * Collections:
 *   db.qualityAssurance.nonCompliance → 'non-conformance'
 *   db.qualityAssurance.tickets       → 'qa-tickets'
 *   db.qualityAssurance.exceptionCodes → 'exception-codes'
 */

import {
  doc, setDoc, updateDoc, addDoc, deleteDoc, collection,
} from 'firebase/firestore';
import { ref, get, set } from 'firebase/database';
import { Timestamp } from 'firebase/firestore';
import { partTypeFromDesc } from '@lib/util';
import { resolveRealtimePath } from '@services/db/helper';
import db from '@services/db';

// ---------------------------------------------------------------------------
// Non-Conformance (NC) Records
// ---------------------------------------------------------------------------

/**
 * Saves a non-conformance record to Firestore.
 */
export const saveNonConformanceRecord = async (id: string, record: any): Promise<void> => {
  const ncRef = doc(db.firestore, db.qualityAssurance.nonCompliance, id);
  await setDoc(ncRef, record);
};

/**
 * Appends a non-conformance event to an order's nonConformanceHistory array.
 */
export const updateOrderNonConformanceHistory = async (
  orderId: string,
  history: any[],
): Promise<void> => {
  const orderRef = doc(db.firestore, db.order.records, orderId);
  await updateDoc(orderRef, { nonConformanceHistory: history });
};

/**
 * Updates the imageUrls on an existing NC record after image upload completes.
 */
export const updateNonConformanceImageUrls = async (id: string, imageUrls: string[]): Promise<void> => {
  const ncRef = doc(db.firestore, db.qualityAssurance.nonCompliance, id);
  await updateDoc(ncRef, { imageUrls });
};

/**
 * Saves updated order items (used during NC auto-cancellation when parts are scrapped).
 */
export const saveOrderItemsAfterScrap = async (orderId: string, orderItems: any[]): Promise<void> => {
  const orderItemsRef = doc(db.firestore, db.order.items, orderId);
  await setDoc(orderItemsRef, { orderItems });
};

/**
 * Updates order fields after a non-conformance is saved (nonConformanceHistory,
 * runners, partCount, orderValue, notes).
 */
export const updateOrderAfterNonConformance = async (
  orderId: string,
  updateObject: Record<string, any>,
): Promise<void> => {
  const orderRef = doc(db.firestore, db.order.records, orderId);
  await updateDoc(orderRef, updateObject);
};

// ---------------------------------------------------------------------------
// QA Tickets
// ---------------------------------------------------------------------------

/**
 * Saves (creates or overwrites) a full QA ticket in Firestore.
 */
export const saveQaTicket = async (ticket: any): Promise<void> => {
  const qaTicketRef = doc(db.firestore, db.qualityAssurance.tickets, ticket.id);
  await setDoc(qaTicketRef, {
    ...ticket,
    type: partTypeFromDesc(ticket.partDescription),
    estimatedCost: ticket.estimatedCost || 0,
    lastUpdatedDate: Timestamp.fromDate(new Date()),
  });
};

/**
 * Reads the current QA ticket record number from the Realtime DB and increments it.
 * Only called when creating a new ticket (URL contains 'new').
 */
export const incrementQaTicketRecordNumber = async (database: any): Promise<void> => {
  const ticketRef = ref(database, `${resolveRealtimePath('/recordNumbers')}/qaTicket`);
  const snapshot = await get(ticketRef);
  const currentRecordNumber = snapshot.val();
  await set(ticketRef, currentRecordNumber + 1);
};

/**
 * Adds image URLs to a QA ticket's nonConformanceImageUrls field.
 */
export const updateQaTicketImages = async (ticketId: string, imageUrls: string[]): Promise<void> => {
  const qaTicketRef = doc(db.firestore, db.qualityAssurance.tickets, ticketId);
  await updateDoc(qaTicketRef, { nonConformanceImageUrls: imageUrls });
};

// ---------------------------------------------------------------------------
// Exception Codes
// ---------------------------------------------------------------------------

/**
 * Updates an existing exception code document (overwrite).
 */
export const saveExceptionCode = async (id: string, data: any): Promise<void> => {
  const docRef = doc(db.firestore, db.qualityAssurance.exceptionCodes, id);
  await setDoc(docRef, data);
};

/**
 * Creates a new exception code document (auto-generated ID).
 */
export const createExceptionCode = async (data: any): Promise<void> => {
  const collectionRef = collection(db.firestore, db.qualityAssurance.exceptionCodes);
  await addDoc(collectionRef, data);
};

/**
 * Permanently deletes an exception code document.
 */
export const deleteExceptionCode = async (id: string): Promise<void> => {
  const docRef = doc(db.firestore, db.qualityAssurance.exceptionCodes, id);
  await deleteDoc(docRef);
};
